/* eslint-disable consistent-return */
import React, { useState, useEffect } from 'react';
import { Autocomplete, useGlobal, globalizationPath } from '@rcg/ui-react';
import {
  Box,
  Grid,
  FormLabel,
  FormControl,
  Select,
  MenuItem,
  useMediaQuery,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import clsx from 'clsx';
import { useQuery, useLazyQuery, useReactiveVar } from '@apollo/client';
import { navigate } from '@gatsbyjs/reach-router';
import Button from '../UiComponents/Button';
import AirLoader from '../AirLoader';
import CounterMenu from '../FlightSearchForm/components/CounterMenu';
import Dictionary from '../../utils/dictionary';
import useDictionary from '../../hooks/useDictionary';
import Cruise from '../../assets/icons/cruise';
import Theme from '../../utils/theme';
import setSearchResults from '../../operations/localState/setSearchResults';
import { setBrowseParams } from '../../operations/localState/setSearchParams';
import setSearchType from '../../operations/localState/setSearchType';
import {
  GET_BROWSE_INITIAL_INFORMATION,
  BROWSE_FLIGHTS_SEARCH,
  LAUNCH_DARKLY_FLAGS_AIR,
} from '../../operations/queries';
import {
  APPLICATION_NAME,
  FLIGHT_SEARCH_TYPE,
  NAVIGATION_PATHS,
  FLIGHT_TYPE_CODES,
  SESSION_STORAGE_FLIGHT_TYPE_CODE,
  ENABLE_ONE_WAY_FLAG,
  SUPPORT_PHONE_NUMBER_LD_FLAG,
  BANNER_TYPES,
  SEARCH_SAME_AIRPORT_ERROR,
  FLIGHT_ERROR,
  ACE_FS_1006_ERROR,
  ACE_FS_1028_ERROR,
  ACE_FS_1019_ERROR,
} from '../../utils/constants';
import { browseParamsVar } from '../../operations/localState/reactiveVariables';
import { getYears, getMonths, getDays } from '../../utils/dateObjetcs';
import { trackSuccessBrowse, trackError } from '../../utils/analytics/browseTracking';
import commonUtils from '../../utils/common';
import NotificationBannerComponent from '../NotificationBanner';
import useStyles from './useStyles';
import useCommonStyles from '../../utils/useCommonStyles';
import {
  setSessionStorageItem,
  getSessionStorageItem,
} from '../../operations/sessionStorage/utils';
import { LDUser } from '../../utils/ldUser';
import getBrand from '../../utils/getBrand';

const BrowseForm = ({ shouldDisplayOnTopVersion, isMobile, handleErrorBanner }) => {
  const classes = useStyles();
  const styles = useCommonStyles;
  const {
    data: { lang, localizedDayjs, market, country },
  } = useGlobal();

  const getDictionary = useDictionary(Dictionary);

  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);
  const browseParams = useReactiveVar(browseParamsVar);
  const searchCriteria = browseParams && browseParams.searchCriteria;

  const placeholderColor = Theme.palette.gray[600];
  const activeColor = Theme.palette.gray[750];
  const isBookFlightsPage = window.location.pathname.includes('bookFlights');

  const [airport, setAirport] = useState(
    !isBookFlightsPage ? (searchCriteria && searchCriteria.departureAirportCode) || '' : '',
  );
  const [ship, setShip] = useState((searchCriteria && searchCriteria.shipCode) || 'none');
  const [selectedYear, setSelectedYear] = useState('none');
  const [shouldLoadInitialYear, setShouldLoadInitialYear] = useState(
    !!(searchCriteria && searchCriteria.selectedYear),
  );
  const [month, setMonth] = useState('none');
  const [shouldLoadInitialMonth, setShouldLoadInitialMonth] = useState(
    !!(searchCriteria && searchCriteria.month),
  );
  const [day, setDay] = useState('none');
  const [shouldLoadInitialDay, setShouldLoadInitialDay] = useState(
    !!(searchCriteria && searchCriteria.day),
  );
  const [shipColor, setShipColor] = useState(placeholderColor);
  const [yearColor, setYearColor] = useState(placeholderColor);
  const [monthColor, setMonthColor] = useState(placeholderColor);
  const [dayColor, setDayColor] = useState(placeholderColor);
  const [sailings, setSailings] = useState([]);
  const [sailingDate, setSailingDate] = useState('');
  const [numberOfGuests, setNumberOfGuests] = useState(
    (searchCriteria && searchCriteria.numberOfGuests) || 2,
  );
  const [shouldSaveResults, setShouldSaveResults] = useState(false);
  const [flightTypeCode, setFlightTypeCode] = useState('');
  const [airportRequiredError, setAirportRequiredError] = useState(false);
  const [shipRequiredError, setShipRequiredError] = useState(false);
  const [selectedYearRequiredError, setSelectedYearRequiredError] = useState(false);
  const [monthRequiredError, setMonthRequiredError] = useState(false);
  const [dayRequiredError, setDayRequiredError] = useState(false);
  const isFullFilledForm =
    airport !== '' &&
    ship !== 'none' &&
    selectedYear !== 'none' &&
    month !== 'none' &&
    day !== 'none';

  const validateRequiredFileds = () => {
    setAirportRequiredError(!airport);
    setShipRequiredError(ship === 'none');
    setSelectedYearRequiredError(selectedYear === 'none');
    setMonthRequiredError(month === 'none');
    setDayRequiredError(day === 'none');
  };
  const isSm = useMediaQuery(Theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(Theme.breakpoints.down('md'));
  const isXl = useMediaQuery(Theme.breakpoints.up('xl'));

  const { data: launchDarklyFlags } = useQuery(LAUNCH_DARKLY_FLAGS_AIR, {
    variables: {
      ldUser: LDUser(),
      language: lang,
      clientApp: APPLICATION_NAME,
      application: APPLICATION_NAME,
      brand: getBrand().ldBrand,
      bookingCountry: country,
      country,
    },
  });

  const currentTabTraking = 'browseflightlookup';

  const {
    loading: browseInitialInformationLoading,
    error: browseInitialInformationError,
    data: browseInitialData,
  } = useQuery(GET_BROWSE_INITIAL_INFORMATION, {
    variables: {
      onlyActive: true,
    },
    skip: !!(
      browseParams &&
      searchCriteria &&
      browseParams.searchSailings &&
      browseParams.airports &&
      browseParams.ships
    ),
  });

  const browseInitialParams = browseInitialData || browseParams;

  const selectedShipSailings =
    browseInitialParams &&
    browseInitialParams.searchSailings &&
    browseInitialParams.searchSailings.shipSailings.find((item) => item.shipCode === ship);

  const [
    getFlights,
    { loading: flightsLoading, error: flightsError, data: browseFlightsSearchData },
  ] = useLazyQuery(BROWSE_FLIGHTS_SEARCH);

  useEffect(() => {
    if (ship !== 'none' && selectedShipSailings) {
      setSailings(selectedShipSailings.sailingDates);
    }
  }, [ship, selectedShipSailings]);

  useEffect(() => {
    if (shouldLoadInitialYear) {
      setShouldLoadInitialYear(false);
      setSelectedYear(searchCriteria.selectedYear);
    }
  }, [ship, searchCriteria, shouldLoadInitialYear]);

  useEffect(() => {
    if (shouldLoadInitialMonth) {
      setShouldLoadInitialMonth(false);
      setMonth(searchCriteria.month);
    }
  }, [ship, searchCriteria, shouldLoadInitialMonth]);

  useEffect(() => {
    if (shouldLoadInitialDay) {
      setShouldLoadInitialDay(false);
      setDay(searchCriteria.day);
    }
  }, [ship, searchCriteria, shouldLoadInitialDay]);

  const airports =
    (browseInitialParams && browseInitialParams.airports && browseInitialParams.airports.nodes) ||
    [];
  const previousSelectedAirport = airports.find((listedAirport) => listedAirport.code === airport);

  const formatShips =
    (browseInitialParams &&
      browseInitialParams.ships &&
      browseInitialParams.ships
        .filter(({ code }) => {
          return browseInitialParams.searchSailings.shipSailings.some(
            (shipSailing) => shipSailing.shipCode === code,
          );
        })
        .map(({ name, code }) => {
          const shipName = name
            .toLowerCase()
            .replace(/(?:^|\s)(?!of|the)\S/g, (a) => a.toUpperCase());
          return { name: shipName, code };
        })) ||
    [];

  const yearsObj = sailings && getYears(sailings);
  const monthsObj = sailings && getMonths(sailings, selectedYear, localizedDayjs);
  const daysArray = sailings && getDays(sailings, selectedYear, month, localizedDayjs);
  const daysObj = daysArray.map((date) => {
    return { date, value: date };
  });

  const handleChangeShip = (event) => {
    setShip(event.target.value);
    setShipRequiredError(false);
    setSelectedYear('none');
    setMonth('none');
    setDay('none');
  };
  const handleChangeYear = (event) => {
    setSelectedYear(event.target.value);
    setSelectedYearRequiredError(false);
    setMonth('none');
    setDay('none');
  };

  const handleChangeMonth = (event) => {
    setMonth(event.target.value);
    setMonthRequiredError(false);
    setDay('none');
  };

  const handleChangeDay = (event) => {
    setDay(event.target.value);
    setDayRequiredError(false);
  };

  const handleAirportChange = (value) => {
    setAirport(value ? value.code : '');
    setAirportRequiredError(false);
  };

  const handleGuests = (count) => {
    setNumberOfGuests(count);
  };

  const handleSubmit = () => {
    if (isFullFilledForm) {
      setShouldSaveResults(true);
      const commonParams = {
        numberOfGuests,
        departureAirportCode: airport,
        shipCode: ship,
        type: flightTypeCode,
      };
      const selectedShipSailingsTemp = { ...selectedShipSailings };
      const shipSearch = {
        label: selectedShipSailingsTemp.shipName
          .toLowerCase()
          .replace(/(?:^|\s)(?!of|the)\S/g, (a) => a.toUpperCase()),
        code: selectedShipSailingsTemp.shipCode,
      };
      setBrowseParams({
        ...browseInitialParams,
        searchCriteria: {
          ...commonParams,
          selectedYear,
          month,
          day,
        },
        ship: shipSearch,
      });
      getFlights({
        variables: {
          ...commonParams,
          sailDate: sailingDate,
        },
      });
      setSearchType({ tab: 'browse' });
    } else {
      validateRequiredFileds();
    }
  };

  let errorMessage = null;
  let internalFlightsError = null;
  if (shouldSaveResults && flightsError && !handleErrorBanner) {
    const getErrorByCode = (errorCode) =>
      flightsError.graphQLErrors.find(({ extensions }) => extensions.code === errorCode);
    if (getErrorByCode(SEARCH_SAME_AIRPORT_ERROR)) {
      trackError('Unsupported', currentTabTraking);
      errorMessage = getDictionary(Dictionary.airBrowsePageFormTypeLabelSameAirpotError.key);
    } else if (getErrorByCode(ACE_FS_1006_ERROR) || getErrorByCode(ACE_FS_1028_ERROR)) {
      trackError('not found', currentTabTraking);

      setSearchResults(
        {
          flightFilters: null,
          matchingFlights: [],
          noResultsError: true,
        },
        true,
      );
      navigate(`${urlPath}${NAVIGATION_PATHS.browseDeparture}`);
    } else {
      trackError('Technical issue', currentTabTraking);
      internalFlightsError = true;
    }
  }

  if (shouldSaveResults && flightsError && handleErrorBanner) {
    const getErrorByCode = (errorCode) =>
      flightsError.graphQLErrors.find(({ extensions }) => extensions.code === errorCode);
    if (getErrorByCode(SEARCH_SAME_AIRPORT_ERROR)) {
      handleErrorBanner({
        title: getDictionary(Dictionary.airBrowsePageFormTypeLabelSameAirpotTitleError.key),
        message: getDictionary(Dictionary.airBrowsePageFormTypeLabelSameAirpotError.key),
      });
    } else if (getErrorByCode(ACE_FS_1006_ERROR) || getErrorByCode(ACE_FS_1028_ERROR)) {
      handleErrorBanner({
        title: getDictionary(Dictionary.airBookFlightNoResultsTitle.key),
        message: `${
          getErrorByCode(ACE_FS_1028_ERROR)
            ? getDictionary(Dictionary.airBookFlightSearchErrorDescription.key)
            : getDictionary(Dictionary.airBookFlightNoResultsDescription.key)
        } ${launchDarklyFlags?.ldFlags[SUPPORT_PHONE_NUMBER_LD_FLAG]} ${getDictionary(
          Dictionary.airFlightSearchErrorAssistance.key,
        )} `,
      });
    } else if (getErrorByCode(ACE_FS_1019_ERROR) || getErrorByCode(FLIGHT_ERROR)) {
      handleErrorBanner({
        title: getDictionary(
          Dictionary.airFlightBookFlightsReservationErrorSailingDateIsNotSupportedTitle.key,
        ),
        message: getDictionary(
          Dictionary.airFlightBookFlightsReservationErrorSailingDateIsNotSupportedDescription.key,
        ),
        type: BANNER_TYPES.warning,
      });
    } else {
      handleErrorBanner({
        title: getDictionary(Dictionary.airFlightBookFlightsReservationErrorInitialDataTitle.key),
        message: getDictionary(
          Dictionary.airFlightBookFlightsReservationErrorInitialDataDescription.key,
        ),
      });
    }
  }

  useEffect(() => {
    if (
      shouldSaveResults &&
      browseFlightsSearchData &&
      browseFlightsSearchData.browseFlightsSearch
    ) {
      setShouldSaveResults(false);
      setSearchResults(
        {
          ...browseFlightsSearchData.browseFlightsSearch,
          flightType: FLIGHT_SEARCH_TYPE.DEPARTURE,
        },
        true,
      );
      trackSuccessBrowse();
      navigate(`${urlPath}${NAVIGATION_PATHS.browseDeparture}`);
    }
    // eslint-disable-next-line
  }, [browseFlightsSearchData]);

  useEffect(() => {
    const colorReplacement = (value) => (value === 'none' ? placeholderColor : activeColor);
    setShipColor(colorReplacement(ship));
    setYearColor(colorReplacement(selectedYear));
    setMonthColor(colorReplacement(month));
    setDayColor(colorReplacement(day));
    setSailingDate(`${selectedYear}-${month}-${day}`);
  }, [ship, selectedYear, month, day, activeColor, placeholderColor]);

  useEffect(() => {
    if (isBookFlightsPage) {
      setAirport('');
      setShip('none');
      setSelectedYear('none');
      setMonth('none');
      setDay('none');
    }
  }, [isBookFlightsPage]);

  useEffect(
    () => {
      if (
        (handleErrorBanner && browseInitialInformationError) ||
        (handleErrorBanner && internalFlightsError)
      ) {
        handleErrorBanner({
          title: getDictionary(
            Dictionary.airFlightBookFlightsReservationErrorTechnicalIssuesTitle.key,
          ),
          message: getDictionary(
            Dictionary.airFlightBookFlightsReservationErrorTechnicalIssuesDescription.key,
          ),
        });
      }
      // eslint-disable-next-line
    },
    browseInitialInformationError,
    internalFlightsError,
  );

  if (
    (!handleErrorBanner && browseInitialInformationError) ||
    (!handleErrorBanner && internalFlightsError)
  )
    navigate(`${urlPath}${NAVIGATION_PATHS.error}`);

  const buttonLabelDictionary =
    shouldDisplayOnTopVersion && !(isXl || isSm)
      ? Dictionary.airSearchLabel
      : Dictionary.airBrowsePageFormCta;

  const flightTypeCodeFromSession = getSessionStorageItem(SESSION_STORAGE_FLIGHT_TYPE_CODE);

  const flightTypeMenu = [
    {
      id: 'flight-type-selector',
      type: 'dropDown',
      disabled: false,
      items: [
        {
          name: getDictionary(Dictionary.airFlightTypeRoundtrip.key),
          key: getDictionary(Dictionary.airFlightTypeRoundtrip.key),
        },
      ],
    },
  ];

  if (launchDarklyFlags?.ldFlags[ENABLE_ONE_WAY_FLAG]) {
    flightTypeMenu[0].items.push({
      name: getDictionary(Dictionary.airFlightTypeOneWay.key),
      key: getDictionary(Dictionary.airFlightTypeOneWay.key),
    });
  }

  const handleFlightTypeSelect = (value) => {
    const oneWayLabel = getDictionary(Dictionary.airFlightTypeOneWay.key);
    if (value.option === oneWayLabel) {
      setFlightTypeCode(FLIGHT_TYPE_CODES.ONE_WAY);
      setSessionStorageItem(SESSION_STORAGE_FLIGHT_TYPE_CODE, FLIGHT_TYPE_CODES.ONE_WAY);
    } else {
      setFlightTypeCode('');
      setSessionStorageItem(SESSION_STORAGE_FLIGHT_TYPE_CODE, FLIGHT_TYPE_CODES.ROUND_TRIP);
    }
  };

  const selectedFlightType =
    flightTypeCode === FLIGHT_TYPE_CODES.ONE_WAY ||
    flightTypeCodeFromSession === FLIGHT_TYPE_CODES.ONE_WAY
      ? {
          name: getDictionary(Dictionary.airFlightTypeOneWay.key),
          key: getDictionary(Dictionary.airFlightTypeOneWay.key),
        }
      : null;

  return (
    <Grid container>
      {errorMessage && (
        <div className={styles.notification}>
          <NotificationBannerComponent
            title="Error!"
            description={errorMessage}
            type="error"
            renderCloseBtn
            isBannerOpen
            onClose={() => {
              errorMessage = null;
            }}
          />
        </div>
      )}
      <AirLoader
        open={browseInitialInformationLoading || flightsLoading}
        status={flightsLoading && getDictionary(Dictionary.loaderSearchFlights.key)}
      />
      <Box
        className={clsx(
          classes.formContainer,
          shouldDisplayOnTopVersion ? classes.formContainerOnTop : '',
        )}>
        <Grid item xs={12}>
          <CounterMenu
            className={classes.counterMenu}
            handleCount={handleGuests}
            lowLimit={1}
            defaultValue={numberOfGuests}
            highLimit={4}
            typeLabel={getDictionary(Dictionary.airBrowsePageFormTypeLabel.key)}
            singleLabel={getDictionary(Dictionary.airBrowsePageFormSingleLabel.key)}
            multipleLabel={getDictionary(Dictionary.airBrowsePageFormMultipleLabel.key)}
            cancelLabel={getDictionary(Dictionary.airBrowsePageFormCancelButton.key)}
            doneLabel={getDictionary(Dictionary.airBrowsePageFormCounterDoneButton.key)}
            menuData={flightTypeMenu}
            selectedOption={handleFlightTypeSelect}
            selectedFlightType={selectedFlightType}
          />
        </Grid>
        <Grid container>
          <Grid
            container
            item
            xs={!shouldDisplayOnTopVersion || isMd ? 12 : 10}
            className={clsx(
              classes.inputsContainer,
              shouldDisplayOnTopVersion ? classes.inputsContainerOnTop : '',
            )}>
            <Grid
              item
              xs={12}
              sm={shouldDisplayOnTopVersion ? 12 : 6}
              md={shouldDisplayOnTopVersion ? 12 : false}
              lg={shouldDisplayOnTopVersion ? 4 : false}>
              <Autocomplete
                className={
                  airportRequiredError
                    ? `${classes.flightsInput} requiered-error`
                    : classes.flightsInput
                }
                id="airports"
                options={airports}
                getInputLabel={commonUtils.fliyingFromOptions}
                getOptionLabel={commonUtils.fliyingFromOptions}
                getSelectedValue={(value) => handleAirportChange(value)}
                placeholder={getDictionary(Dictionary.airFromPlaceholder.key)}
                label={getDictionary(Dictionary.airFromLabel.key)}
                incomingValue={
                  previousSelectedAirport &&
                  previousSelectedAirport.code &&
                  `${previousSelectedAirport.city} (${previousSelectedAirport.code})`
                }
                noResultsErrorMessage={getDictionary(Dictionary.airNoResultsErrorMessage.key)}
              />
              {airportRequiredError && (
                <div className={classes.requiredFieldError}>
                  {getDictionary(Dictionary.airFormReservationFieldAirportRequiredError.key)}
                </div>
              )}
            </Grid>
            <Grid
              item
              xs={12}
              sm={shouldDisplayOnTopVersion ? 12 : 6}
              md={shouldDisplayOnTopVersion ? 12 : false}
              lg={shouldDisplayOnTopVersion ? 4 : false}>
              <Box className={classes.shipContainer}>
                <FormControl variant="outlined" className={classes.shipsInput}>
                  <FormLabel className={classes.legend} component="legend">
                    {getDictionary(Dictionary.airBrowsePageFormShipLabel.key)}
                  </FormLabel>
                  <Box
                    className={
                      shipRequiredError
                        ? `${classes.mobileShipInput} requiered-error`
                        : classes.mobileShipInput
                    }>
                    <Cruise />
                    {!isMobile && (
                      <Select
                        data-testid="ship-input"
                        style={{ color: shipColor }}
                        IconComponent={KeyboardArrowDownIcon}
                        classes={{ select: classes.select }}
                        value={ship}
                        MenuProps={{
                          classes: {
                            paper: classes.shipsListboxPaper,
                          },
                          getContentAnchorEl: null,
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                        }}
                        onChange={handleChangeShip}>
                        <MenuItem value="none" disabled selected>
                          {getDictionary(Dictionary.airBrowsePageFormShipPlaceholder.key)}
                        </MenuItem>
                        {formatShips &&
                          formatShips.map((item) => (
                            <MenuItem key={item.code} value={item.code}>
                              {item.name}
                            </MenuItem>
                          ))}
                      </Select>
                    )}

                    {isMobile && (
                      <Select
                        style={{ color: shipColor }}
                        IconComponent={KeyboardArrowDownIcon}
                        native
                        classes={{ select: classes.select }}
                        value={ship}
                        inputProps={{ 'data-testid': 'mobile-ship-select' }}
                        onChange={handleChangeShip}>
                        <option value="none" disabled defaultValue>
                          {getDictionary(Dictionary.airBrowsePageFormShipPlaceholder.key)}
                        </option>
                        {formatShips &&
                          formatShips.map((item) => (
                            <option key={item.code} value={item.code}>
                              {item.name}
                            </option>
                          ))}
                      </Select>
                    )}
                  </Box>
                  {shipRequiredError && (
                    <div className={classes.requiredFieldError}>
                      {getDictionary(Dictionary.airFormReservationFieldShipRequiredError.key)}
                    </div>
                  )}
                </FormControl>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              sm={shouldDisplayOnTopVersion ? 12 : 6}
              md={shouldDisplayOnTopVersion ? 12 : false}
              lg={shouldDisplayOnTopVersion ? 4 : false}>
              <Box className={classes.dateContainer}>
                <FormLabel className={classes.legend} component="legend">
                  {getDictionary(Dictionary.airBrowsePageFormDatesLabel.key)}
                </FormLabel>

                {!isMobile && (
                  <Box className={classes.inputsDateContainer}>
                    <FormControl fullWidth variant="outlined" className={classes.formControl}>
                      <Select
                        data-testid="year-input"
                        style={{ color: yearColor }}
                        IconComponent={KeyboardArrowDownIcon}
                        value={selectedYear}
                        inputProps={{ 'data-testid': 'year-select' }}
                        disabled={ship === 'none' || getYears.length === 0}
                        classes={{ select: classes.selectDates }}
                        MenuProps={{
                          classes: {
                            paper: classes.dateListboxPaper,
                          },
                          getContentAnchorEl: null,
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                        }}
                        className={
                          selectedYearRequiredError
                            ? `${classes.dateInput} requiered-error`
                            : classes.dateInput
                        }
                        onChange={handleChangeYear}>
                        <MenuItem value="none" disabled selected>
                          {getDictionary(Dictionary.airBrowsePageFormYearPlaceholder.key)}
                        </MenuItem>
                        {yearsObj.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {selectedYearRequiredError && (
                        <div className={classes.requiredFieldError}>
                          {getDictionary(Dictionary.airFormReservationFieldYearRequiredError.key)}
                        </div>
                      )}
                    </FormControl>
                    <FormControl fullWidth variant="outlined" className={classes.formControl}>
                      <Select
                        style={{ color: monthColor }}
                        IconComponent={KeyboardArrowDownIcon}
                        value={month}
                        disabled={selectedYear === 'none'}
                        classes={{ select: classes.selectDates }}
                        MenuProps={{
                          classes: {
                            paper: classes.dateListboxPaper,
                          },
                          getContentAnchorEl: null,
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                        }}
                        className={
                          monthRequiredError
                            ? `${classes.dateInput} requiered-error`
                            : classes.dateInput
                        }
                        onChange={handleChangeMonth}>
                        <MenuItem value="none" disabled selected>
                          {getDictionary(Dictionary.airBrowsePageFormMonthPlaceholder.key)}
                        </MenuItem>
                        {monthsObj.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {monthRequiredError && (
                        <div className={classes.requiredFieldError}>
                          {getDictionary(Dictionary.airFormReservationFieldMonthRequiredError.key)}
                        </div>
                      )}
                    </FormControl>
                    <FormControl fullWidth variant="outlined" className={classes.formControl}>
                      <Select
                        style={{ color: dayColor }}
                        classes={{ select: classes.selectDates }}
                        IconComponent={KeyboardArrowDownIcon}
                        value={day}
                        disabled={month === 'none'}
                        MenuProps={{
                          classes: {
                            paper: classes.dateListboxPaper,
                          },
                          getContentAnchorEl: null,
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                        }}
                        className={
                          dayRequiredError
                            ? `${classes.dateInput} requiered-error`
                            : classes.dateInput
                        }
                        onChange={handleChangeDay}>
                        <MenuItem value="none" disabled selected>
                          {getDictionary(Dictionary.airBrowsePageFormDayPlaceholder.key)}
                        </MenuItem>
                        {daysObj.map((item) => (
                          <MenuItem key={item.value} value={item.value}>
                            {item.date}
                          </MenuItem>
                        ))}
                      </Select>
                      {dayRequiredError && (
                        <div className={classes.requiredFieldError}>
                          {getDictionary(Dictionary.airFormReservationFieldDayRequiredError.key)}
                        </div>
                      )}
                    </FormControl>
                  </Box>
                )}

                {isMobile && (
                  <Box className={classes.inputsDateContainer}>
                    <FormControl fullWidth variant="outlined" className={classes.formControl}>
                      <Select
                        style={{ color: yearColor }}
                        native
                        IconComponent={KeyboardArrowDownIcon}
                        value={selectedYear}
                        disabled={ship === 'none'}
                        classes={{ select: classes.selectDates }}
                        className={
                          selectedYearRequiredError
                            ? `${classes.dateInput} requiered-error`
                            : classes.dateInput
                        }
                        inputProps={{ 'data-testid': 'mobile-year-select' }}
                        onChange={handleChangeYear}>
                        <option value="" disabled defaultValue>
                          {getDictionary(Dictionary.airBrowsePageFormYearPlaceholder.key)}
                        </option>
                        {yearsObj.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </Select>
                      {selectedYearRequiredError && (
                        <div className={classes.requiredFieldError}>
                          {getDictionary(Dictionary.airFormReservationFieldYearRequiredError.key)}
                        </div>
                      )}
                    </FormControl>
                    <FormControl fullWidth variant="outlined" className={classes.formControl}>
                      <Select
                        style={{ color: monthColor }}
                        native
                        IconComponent={KeyboardArrowDownIcon}
                        value={month}
                        inputProps={{ 'data-testid': 'mobile-month-select' }}
                        disabled={selectedYear === 'none'}
                        classes={{ select: classes.selectDates }}
                        className={
                          monthRequiredError
                            ? `${classes.dateInput} requiered-error`
                            : classes.dateInput
                        }
                        onChange={handleChangeMonth}>
                        <option value="none" disabled defaultValue>
                          {getDictionary(Dictionary.airBrowsePageFormMonthPlaceholder.key)}
                        </option>
                        {monthsObj.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </Select>
                      {monthRequiredError && (
                        <div className={classes.requiredFieldError}>
                          {getDictionary(Dictionary.airFormReservationFieldMonthRequiredError.key)}
                        </div>
                      )}
                    </FormControl>
                    <FormControl fullWidth variant="outlined" className={classes.formControl}>
                      <Select
                        style={{ color: dayColor }}
                        native
                        classes={{ select: classes.selectDates }}
                        IconComponent={KeyboardArrowDownIcon}
                        value={day}
                        inputProps={{ 'data-testid': 'mobile-day-select' }}
                        disabled={month === 'none'}
                        className={
                          dayRequiredError
                            ? `${classes.dateInput} requiered-error`
                            : classes.dateInput
                        }
                        onChange={handleChangeDay}>
                        <option value="none" disabled defaultValue>
                          {getDictionary(Dictionary.airBrowsePageFormDayPlaceholder.key)}
                        </option>
                        {daysObj.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.date}
                          </option>
                        ))}
                      </Select>
                      {dayRequiredError && (
                        <div className={classes.requiredFieldError}>
                          {getDictionary(Dictionary.airFormReservationFieldDayRequiredError.key)}
                        </div>
                      )}
                    </FormControl>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
          {shouldDisplayOnTopVersion && !isSm && (
            <Grid item sm={12} lg={2} className={clsx(classes.gridButtonWrapper, 'buttonWrapper')}>
              <Button type="submit" onClick={handleSubmit} variant="contained" color="primary">
                {getDictionary(buttonLabelDictionary.key)}
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>
      {(!shouldDisplayOnTopVersion || isSm) && (
        <Grid item sm={12} className={classes.footerForm}>
          <Button
            className={classes.browseButton}
            type="submit"
            onClick={handleSubmit}
            variant="contained"
            color="primary">
            {getDictionary(buttonLabelDictionary.key)}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

export default BrowseForm;
